
import { comprehensiveQuizAuditor } from './comprehensiveQuizAudit';
import { standardizedQuizGenerator } from './standardizedQuizGenerator';
import { getAllCourses } from '@/data/courses';
import { SQL_COURSES } from '@/data/sql';
import { PYTHON_COURSES } from '@/data/python';
import { POWERBI_COURSES } from '@/data/powerbi';

export interface QuizFixResult {
  courseId: string;
  moduleId: string;
  action: 'added' | 'updated' | 'skipped';
  reason: string;
  quizGenerated?: any;
}

export interface QuizSystemFixReport {
  totalModulesProcessed: number;
  quizzesAdded: number;
  quizzesUpdated: number;
  modulesSkipped: number;
  results: QuizFixResult[];
  errors: string[];
}

export class QuizSystemFixer {
  
  public async fixAllMissingQuizzes(): Promise<QuizSystemFixReport> {
    console.log('🔧 Starting automated quiz system fix...');
    
    const audit = comprehensiveQuizAuditor.auditAllQuizzes();
    const results: QuizFixResult[] = [];
    const errors: string[] = [];
    
    let totalModulesProcessed = 0;
    let quizzesAdded = 0;
    let quizzesUpdated = 0;
    let modulesSkipped = 0;

    // Process each course that has missing quizzes
    for (const courseResult of audit.courseResults) {
      if (courseResult.modulesWithoutQuizzes.length > 0) {
        console.log(`🎯 Fixing ${courseResult.courseId} (${courseResult.modulesWithoutQuizzes.length} missing quizzes)`);
        
        try {
          const courseFixResult = await this.fixCourseQuizzes(
            courseResult.courseId, 
            courseResult.courseType, 
            courseResult.modulesWithoutQuizzes
          );
          
          results.push(...courseFixResult);
          
          // Update counters
          courseFixResult.forEach(result => {
            totalModulesProcessed++;
            if (result.action === 'added') quizzesAdded++;
            else if (result.action === 'updated') quizzesUpdated++;
            else modulesSkipped++;
          });
          
        } catch (error) {
          errors.push(`Failed to fix ${courseResult.courseId}: ${error}`);
        }
      }
    }

    return {
      totalModulesProcessed,
      quizzesAdded,
      quizzesUpdated,
      modulesSkipped,
      results,
      errors
    };
  }

  private async fixCourseQuizzes(
    courseId: string, 
    courseType: string, 
    moduleIds: string[]
  ): Promise<QuizFixResult[]> {
    const results: QuizFixResult[] = [];
    
    for (const moduleId of moduleIds) {
      try {
        const quizTemplate = standardizedQuizGenerator.generateQuizForModule(
          moduleId, 
          courseType, 
          `${courseType.charAt(0).toUpperCase() + courseType.slice(1)} Module`
        );
        
        // Create the quiz object
        const generatedQuiz = {
          questions: quizTemplate.questions,
          passThreshold: quizTemplate.passThreshold,
          timeLimit: quizTemplate.timeLimit
        };
        
        results.push({
          courseId,
          moduleId,
          action: 'added',
          reason: `Generated ${quizTemplate.questions.length} questions for ${courseType} module`,
          quizGenerated: generatedQuiz
        });
        
      } catch (error) {
        results.push({
          courseId,
          moduleId,
          action: 'skipped',
          reason: `Error generating quiz: ${error}`
        });
      }
    }
    
    return results;
  }

  public generateQuizFiles(): { [filePath: string]: string } {
    console.log('📝 Generating quiz files for missing quizzes...');
    
    const audit = comprehensiveQuizAuditor.auditAllQuizzes();
    const files: { [filePath: string]: string } = {};
    
    // Group modules by course type for efficient file generation
    const modulesByCourseType: { [courseType: string]: Array<{ courseId: string; moduleId: string }> } = {};
    
    audit.courseResults.forEach(courseResult => {
      if (courseResult.modulesWithoutQuizzes.length > 0) {
        if (!modulesByCourseType[courseResult.courseType]) {
          modulesByCourseType[courseResult.courseType] = [];
        }
        
        courseResult.modulesWithoutQuizzes.forEach(moduleId => {
          modulesByCourseType[courseResult.courseType].push({
            courseId: courseResult.courseId,
            moduleId
          });
        });
      }
    });

    // Generate quiz files for each course type
    Object.entries(modulesByCourseType).forEach(([courseType, modules]) => {
      const filePath = `src/data/${courseType}/generated-quizzes.ts`;
      files[filePath] = this.generateQuizFileContent(courseType, modules);
    });

    return files;
  }

  private generateQuizFileContent(
    courseType: string, 
    modules: Array<{ courseId: string; moduleId: string }>
  ): string {
    let content = `// Auto-generated quiz content for ${courseType} courses\n`;
    content += `// Generated on: ${new Date().toISOString()}\n\n`;
    
    content += `export const GENERATED_${courseType.toUpperCase()}_QUIZZES = {\n`;
    
    modules.forEach(({ courseId, moduleId }) => {
      const quizTemplate = standardizedQuizGenerator.generateQuizForModule(moduleId, courseType);
      
      content += `  '${moduleId}': {\n`;
      content += `    questions: [\n`;
      
      quizTemplate.questions.forEach((question, index) => {
        content += `      {\n`;
        content += `        id: '${question.id}',\n`;
        content += `        question: '${question.question}',\n`;
        content += `        options: [\n`;
        question.options.forEach(option => {
          content += `          '${option}',\n`;
        });
        content += `        ],\n`;
        content += `        correctAnswer: ${question.correctAnswer},\n`;
        content += `        difficulty: '${question.difficulty}',\n`;
        content += `        explanation: '${question.explanation}',\n`;
        content += `        category: '${question.category}',\n`;
        content += `        points: ${question.points},\n`;
        content += `        type: '${question.type}'\n`;
        content += `      }${index < quizTemplate.questions.length - 1 ? ',' : ''}\n`;
      });
      
      content += `    ],\n`;
      content += `    passThreshold: ${quizTemplate.passThreshold},\n`;
      content += `    timeLimit: ${quizTemplate.timeLimit}\n`;
      content += `  },\n`;
    });
    
    content += `};\n\n`;
    
    content += `// Helper function to get quiz for a module\n`;
    content += `export const get${courseType.charAt(0).toUpperCase() + courseType.slice(1)}Quiz = (moduleId: string) => {\n`;
    content += `  return GENERATED_${courseType.toUpperCase()}_QUIZZES[moduleId];\n`;
    content += `};\n`;
    
    return content;
  }

  public generateFixReport(): string {
    const audit = comprehensiveQuizAuditor.auditAllQuizzes();
    
    let report = '🔧 QUIZ SYSTEM FIX PLAN\n';
    report += '=' .repeat(40) + '\n\n';
    
    report += '📊 CURRENT STATUS:\n';
    report += `• Total modules missing quizzes: ${audit.modulesWithoutQuizzes}\n`;
    report += `• Total courses affected: ${audit.courseResults.filter(c => c.modulesWithoutQuizzes.length > 0).length}\n`;
    report += `• Overall coverage: ${((audit.modulesWithQuizzes / audit.totalModules) * 100).toFixed(1)}%\n\n`;

    report += '🎯 MODULES TO FIX BY COURSE TYPE:\n';
    const modulesByCourseType: { [key: string]: number } = {};
    
    audit.courseResults.forEach(course => {
      if (course.modulesWithoutQuizzes.length > 0) {
        if (!modulesByCourseType[course.courseType]) {
          modulesByCourseType[course.courseType] = 0;
        }
        modulesByCourseType[course.courseType] += course.modulesWithoutQuizzes.length;
      }
    });

    Object.entries(modulesByCourseType).forEach(([courseType, count]) => {
      report += `• ${courseType.toUpperCase()}: ${count} modules\n`;
    });
    report += '\n';

    report += '📋 DETAILED BREAKDOWN:\n';
    audit.courseResults.forEach(course => {
      if (course.modulesWithoutQuizzes.length > 0) {
        report += `\n${course.courseId} (${course.courseType}):\n`;
        report += `  Missing quizzes: ${course.modulesWithoutQuizzes.join(', ')}\n`;
      }
    });

    report += '\n✅ EXECUTION PLAN:\n';
    report += '1. Generate standardized quizzes for each course type\n';
    report += '2. Create quiz files in appropriate data directories\n';
    report += '3. Update module imports to include new quizzes\n';
    report += '4. Validate all generated content\n';
    report += '5. Run quality assurance checks\n';

    return report;
  }
}

export const quizSystemFixer = new QuizSystemFixer();
